if (!customElements.get('best-sellers-carousel')) {

  const CATEGORY_MAP = {
    'muebles-plastico-exterior': [
      'set-muebles-plastico-jardin-terraza',
      'mesa-plastico-jardin',
      'sillas-plastico-jardin',
      'sillones-multiposicion-plastico-jardin',
      'tumbonas-plastico',
      'baules-para-exterior'
    ],
    'neveras-portatiles-plastico': [],
    'utensilios-limpieza': [
      'cubos-basura',
      'cubos-fregona'
    ],
    'menaje-cocina': [
      'tuppers-hermeticos-plastico'
    ]
  };

  const EXCLUDED_COLLECTIONS = [
    'destacada',
    'seleccion-top-para-el-hogar',
    'hasta-el-45-en-seleccion-de-mobiliario-de-exterior',
    'outlet',
    'precio-original',
    'rebajas-de-verano',
    'conjuntos-de-terraza',
    'escobas-goma'
  ];

  class BestSellersCarousel extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this), 500);
    }

    detectCategories(productCollections) {
      let subcategory = null;
      let parentCategory = null;

      for (const col of productCollections) {
        if (EXCLUDED_COLLECTIONS.includes(col)) continue;

        for (const [parent, children] of Object.entries(CATEGORY_MAP)) {
          if (children.includes(col)) {
            subcategory = col;
            parentCategory = parent;
            break;
          }
          if (parent === col && children.length === 0) {
            subcategory = col;
            parentCategory = col;
            break;
          }
        }
        if (subcategory) break;
      }

      return { subcategory, parentCategory };
    }

    async getProductCollections(productHandles, storeDomain, token) {
      const queries = productHandles.map((handle, i) => `
        p${i}: product(handle: "${handle}") {
          collections(first: 10) {
            nodes { handle }
          }
        }
      `).join('\n');

      const response = await fetch(`https://${storeDomain}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query: `{ ${queries} }` })
      });

      const { data } = await response.json();
      const allCollections = [];

      for (const key of Object.keys(data || {})) {
        const cols = data[key]?.collections?.nodes || [];
        cols.forEach(c => {
          if (!allCollections.includes(c.handle)) {
            allCollections.push(c.handle);
          }
        });
      }

      return allCollections;
    }

    async fetchBestSellers(collectionHandle, limit, excludeHandles, storeDomain, token) {
      const query = `
        query BestSellers($handle: String!, $limit: Int!) {
          collection(handle: $handle) {
            products(first: $limit, sortKey: BEST_SELLING, filters: [{available: true}]) {
              nodes {
                handle
                title
                priceRange {
                  minVariantPrice { amount currencyCode }
                }
                compareAtPriceRange {
                  minVariantPrice { amount currencyCode }
                }
                featuredImage {
                  url(transform: { maxWidth: 600 })
                  altText
                  width
                  height
                }
                collections(first: 10) {
                  nodes { handle }
                }
                variants(first: 10) {
                  nodes {
                    id
                    availableForSale
                    quantityAvailable
                    selectedOptions {
                      name
                      value
                    }
                    image {
                      id
                      url(transform: { maxWidth: 600, maxHeight: 600 })
                      altText
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`https://${storeDomain}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({
          query,
          variables: { handle: collectionHandle, limit }
        })
      });

      const { data } = await response.json();
      let products = data?.collection?.products?.nodes || [];

      if (excludeHandles && excludeHandles.length > 0) {
        products = products.filter(p => !excludeHandles.includes(p.handle));
      }

      return products;
    }

    async fetchGlobalBestSellers(limit, excludeHandles, storeDomain, token) {
      const allCategories = Object.keys(CATEGORY_MAP);
      let products = [];

      for (const cat of allCategories) {
        if (products.length >= limit) break;
        const fetched = await this.fetchBestSellers(cat, limit, excludeHandles, storeDomain, token);
        for (const p of fetched) {
          if (!excludeHandles.includes(p.handle) && !products.find(x => x.handle === p.handle)) {
            products.push(p);
          }
        }
      }

      return products.slice(0, limit);
    }

    async init() {
      const mode = this.dataset.mode;
      const productHandle = this.dataset.productHandle;
      const productCollections = this.dataset.collections
        ? this.dataset.collections.split(',')
        : [];
      const cartProductHandles = this.dataset.cartProducts
        ? this.dataset.cartProducts.split(',')
        : [];
      const limit = parseInt(this.dataset.limit) || 5;
      const storeDomain = this.dataset.domain;
      const token = 'b0aa99e16a486505b043e91117c6f07a';

      try {
        let products = [];

        if (mode === 'similar') {
          const { subcategory } = this.detectCategories(productCollections);

          if (!subcategory) {
            this.closest('section') && (this.closest('section').hidden = true);
            return;
          }

          products = await this.fetchBestSellers(
            subcategory, limit + 1, [productHandle], storeDomain, token
          );

          if (products.length === 0) {
            this.closest('section') && (this.closest('section').hidden = true);
            return;
          }

          products = products.slice(0, limit);

        } else if (mode === 'combinalo') {
          const { subcategory, parentCategory } = this.detectCategories(productCollections);
          const excludeHandles = [productHandle];

          if (subcategory && parentCategory) {
            // Query a la categoría principal con BEST_SELLING
            // Pedimos más productos para tener margen al filtrar
            const allFromParent = await this.fetchBestSellers(
              parentCategory, 50, excludeHandles, storeDomain, token
            );

            // Filtrar los que pertenezcan a la subcategoría actual
            products = allFromParent.filter(p =>
              !p.collections.nodes.some(c => c.handle === subcategory)
            ).slice(0, limit);
          }

          // Rellenar con globales si faltan
          if (products.length < limit) {
            const globalExclude = [
              ...excludeHandles,
              ...products.map(p => p.handle)
            ];
            const global = await this.fetchGlobalBestSellers(
              limit - products.length + 5,
              globalExclude,
              storeDomain,
              token
            );
            for (const p of global) {
              if (products.length >= limit) break;
              if (!products.find(x => x.handle === p.handle)) products.push(p);
            }
          }

          products = products.slice(0, limit);

          if (products.length === 0) {
            this.closest('section') && (this.closest('section').hidden = true);
            return;
          }

        } else if (mode === 'cart') {
          if (cartProductHandles.length === 0) {
            this.closest('.cart-page-recommendations') && (this.closest('.cart-page-recommendations').hidden = true);
            return;
          }

          // Obtener colecciones de todos los productos del carrito
          const cartCollections = await this.getProductCollections(cartProductHandles, storeDomain, token);

          // Detectar subcategorías y categorías principales en el carrito
          const cartSubcategories = [];
          const cartParentCategories = [];

          for (const col of cartCollections) {
            for (const [parent, children] of Object.entries(CATEGORY_MAP)) {
              if (children.includes(col) && !cartSubcategories.includes(col)) {
                cartSubcategories.push(col);
                if (!cartParentCategories.includes(parent)) {
                  cartParentCategories.push(parent);
                }
              }
              if (parent === col && children.length === 0 && !cartSubcategories.includes(col)) {
                cartSubcategories.push(col);
                if (!cartParentCategories.includes(parent)) {
                  cartParentCategories.push(parent);
                }
              }
            }
          }

          const excludeHandles = [...cartProductHandles];

          // Para cada categoría principal presente en el carrito,
          // traer sus best sellers y excluir las subcategorías que ya están en el carrito
          for (const parent of cartParentCategories) {
            if (products.length >= limit) break;

            const subcatsInCart = CATEGORY_MAP[parent]
              ? CATEGORY_MAP[parent].filter(s => cartSubcategories.includes(s))
              : [];

            const allFromParent = await this.fetchBestSellers(
              parent, 50, excludeHandles, storeDomain, token
            );

            const filtered = allFromParent.filter(p =>
              !p.collections.nodes.some(c => subcatsInCart.includes(c.handle))
            );

            for (const p of filtered) {
              if (products.length >= limit) break;
              if (!products.find(x => x.handle === p.handle)) {
                products.push(p);
                excludeHandles.push(p.handle);
              }
            }
          }

          // También añadir productos de categorías principales NO presentes en el carrito
          const otherParents = Object.keys(CATEGORY_MAP).filter(
            p => !cartParentCategories.includes(p)
          );

          for (const parent of otherParents) {
            if (products.length >= limit) break;

            const fetched = await this.fetchBestSellers(
              parent, limit, excludeHandles, storeDomain, token
            );

            for (const p of fetched) {
              if (products.length >= limit) break;
              if (!products.find(x => x.handle === p.handle)) {
                products.push(p);
                excludeHandles.push(p.handle);
              }
            }
          }

          // Rellenar con globales si faltan
          if (products.length < limit) {
            const globalExclude = [...excludeHandles, ...products.map(p => p.handle)];
            const global = await this.fetchGlobalBestSellers(
              limit - products.length + 5,
              globalExclude,
              storeDomain,
              token
            );
            for (const p of global) {
              if (products.length >= limit) break;
              if (!products.find(x => x.handle === p.handle)) products.push(p);
            }
          }

          products = products.slice(0, limit);

          if (products.length === 0) {
            this.closest('.cart-page-recommendations') && (this.closest('.cart-page-recommendations').hidden = true);
            return;
          }
        }

        if (mode === 'cart') {
          this.renderCartProducts(products);
        } else {
          this.renderProducts(products);
        }

        const carousel = this.querySelector('carousel-slider');
        if (carousel && typeof carousel.init === 'function') {
          carousel.init();
        }

        window.initLazyImages && window.initLazyImages();

      } catch (error) {
        console.error('BestSellersCarousel error:', error);
      }
    }

    formatPrice(amount, currencyCode) {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: currencyCode
      }).format(parseFloat(amount));
    }

    renderBadge(price, compareAt) {
      if (!compareAt) return '';
      const priceVal = parseFloat(price.amount);
      const compareVal = parseFloat(compareAt.amount);
      if (compareVal <= priceVal) return '';

      return `
        <div class="product-label-container absolute top-0 start">
          <div class="product-label-wrapper flex">
            <span class="product-label product-label--sale">
              <svg class="icon icon--price_tag" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation"><path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"></path><path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"></path></svg>
              -${Math.round((1 - priceVal / compareVal) * 100)}%
            </span>
          </div>
        </div>`;
    }

    renderSwatches(product) {
      const variants = product.variants?.nodes || [];
      if (variants.length <= 1) return '';

      const colorNames = ['color', 'colour', 'colore', 'farbe', 'couleur'];
      const uniqueVariants = [];
      const seen = new Set();

      for (const v of variants) {
        const colorOpt = v.selectedOptions.find(o => colorNames.includes(o.name.toLowerCase()));
        if (colorOpt && !seen.has(colorOpt.value)) {
          seen.add(colorOpt.value);
          const colorVariants = variants.filter(x =>
            x.selectedOptions.some(o => colorNames.includes(o.name.toLowerCase()) && o.value === colorOpt.value)
          );
          const available = colorVariants.some(x =>
            x.availableForSale && (x.quantityAvailable === null || x.quantityAvailable > 0)
          );
          uniqueVariants.push({
            value: colorOpt.value,
            image: v.image,
            imageId: v.image?.id?.split('/').pop() || '',
            variantId: v.id?.split('/').pop() || '',
            unavailable: !available
          });
        }
      }

      if (uniqueVariants.length <= 1) return '';

      const swatchId = `bsc-${product.handle}`;

      return `<div class="card__swatches pb-1 pt-3 flex items-start relative justify-center">${uniqueVariants.map((v, i) => {
        const imgSrc = v.image?.url || '';
        return `
          <input type="radio" class="opt-btn visually-hidden js-option${v.unavailable ? ' is-unavailable' : ''}" name="${swatchId}-color" id="${swatchId}-opt-${i}" value="${v.value}" data-variant-id="${v.variantId}"${v.imageId ? ` data-media-id="${v.imageId}"` : ''}${i === 0 ? ' checked' : ''}>
          <label class="opt-label opt-label--swatch relative swatch-shape--square swatch-shape--not-circle swatch--variant-image" title="${v.value}" data-swatch="" for="${swatchId}-opt-${i}">
            ${imgSrc ? `<img loading="lazy" class="img-fit bg-theme-bg" src="${imgSrc}" height="52" width="52" alt="${v.value}">` : ''}
            <span class="visually-hidden">${v.value}</span>
          </label>`;
      }).join('')}</div>`;
    }

    renderVariantImages(product) {
      const variants = product.variants?.nodes || [];
      return variants.map(v => {
        const image = v.image;
        if (!image) return '';
        const mediaId = image.id?.split('/').pop();
        if (!mediaId) return '';
        return `
          <img
            src="${image.url}"
            alt="${image.altText || ''}"
            loading="lazy"
            width="${image.width || 600}"
            height="${image.height || 600}"
            class="img-fit img-fit--contain card__main-image"
            data-media-id="${mediaId}"
            hidden
          >`;
      }).join('');
    }

    renderProducts(products) {
      const list = this.querySelector('.js-best-sellers-list');
      if (!list) return;

      list.innerHTML = products.map(product => {
        const url = `/products/${product.handle}`;
        const title = product.title;
        const price = product.priceRange.minVariantPrice;
        const compareAt = product.compareAtPriceRange?.minVariantPrice;
        const isOnSale = compareAt && parseFloat(compareAt.amount) > parseFloat(price.amount);
        const priceFormatted = this.formatPrice(price.amount, price.currencyCode);
        const compareAtFormatted = isOnSale
          ? this.formatPrice(compareAt.amount, compareAt.currencyCode)
          : null;

        const image = product.featuredImage
          ? `<img
              src="${product.featuredImage.url}"
              alt="${product.featuredImage.altText || title}"
              loading="lazy"
              width="${product.featuredImage.width || 600}"
              height="${product.featuredImage.height || 600}"
              class="img-fit img-fit--contain card__main-image"
            >`
          : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5" class="media__placeholder img-fit"><path fill="#EBEBEB" d="M324.5 212.7H201c-12.3 0-21.3 3-27.3 9s-9 14.9-9 26.8v88.3c0 11.9 3 20.8 9 26.8s15 9 27.3 9h123.5c12.3 0 21.3-3 27.3-9s9-14.9 9-26.8v-88.3c0-11.9-3-20.8-9-26.8s-15-9-27.3-9z"/></svg>`;

        const variantImages = this.renderVariantImages(product);

        const priceHtml = isOnSale
          ? `<div class="price price--on-sale price--sale-color price--bottom">
              <div class="price__default">
                <span class="price__from">Desde</span>
                <span class="price__current">${priceFormatted}</span>
                <s class="price__was">${compareAtFormatted}</s>
              </div>
            </div>`
          : `<div class="price price--bottom">
              <div class="price__default">
                <span class="price__current">${priceFormatted}</span>
              </div>
            </div>`;

        return `
          <li class="slider__item">
            <product-card class="card card--product h-full card--no-lines relative flex">
              <div class="card__media relative">
                <a href="${url}"
                   aria-label="${title}"
                   class="media block relative js-prod-link"
                   style="padding-top: 100%;"
                   tabindex="-1">
                  ${image}
                  ${variantImages}
                </a>
                ${this.renderBadge(price, compareAt)}
              </div>
              <div class="card__info-container flex flex-col flex-auto relative">
                <div class="card__info w-full">
                  <div class="card__info-inner flex flex-col h-full text-center w-full">
                    ${this.renderSwatches(product)}
                    <p class="card__title font-bold mt-1 mb-0">
                      <a href="${url}" class="card-link text-current js-prod-link">${title}</a>
                    </p>
                    <div class="product-card__price flex grow items-end justify-center">
                      ${priceHtml}
                    </div>
                  </div>
                </div>
                <div class="card__quick-add mob:card__quick-add--below desktop:card__quick-add--below">
                  <div class="no-js-hidden">
                    <a href="${url}" class="btn btn--primary w-full quick-add-view-btn">
                      Ver producto
                    </a>
                  </div>
                </div>
              </div>
            </product-card>
          </li>
        `;
      }).join('');
    }

    renderCartProducts(products) {
      const list = this.querySelector('.js-best-sellers-list');
      if (!list) return;

      list.innerHTML = products.map(product => {
        const url = `/products/${product.handle}`;
        const title = product.title;
        const price = product.priceRange.minVariantPrice;
        const compareAt = product.compareAtPriceRange?.minVariantPrice;
        const isOnSale = compareAt && parseFloat(compareAt.amount) > parseFloat(price.amount);
        const priceFormatted = this.formatPrice(price.amount, price.currencyCode);
        const compareAtFormatted = isOnSale
          ? this.formatPrice(compareAt.amount, compareAt.currencyCode)
          : null;
        const firstVariant = product.variants?.nodes?.[0];
        const variantId = firstVariant?.id?.split('/').pop() || '';

        const image = product.featuredImage
          ? `<div class="media relative" style="padding-top: 100.0%;">
              <img src="${product.featuredImage.url}" alt="${product.featuredImage.altText || title}" loading="lazy" width="176" height="176" class="img-fit">
            </div>`
          : `<div class="media relative" style="padding-top: 100.0%;">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5" class="media__placeholder dark"><path fill="#EBEBEB" d="M324.5 212.7H201c-12.3 0-21.3 3-27.3 9s-9 14.9-9 26.8v88.3c0 11.9 3 20.8 9 26.8s15 9 27.3 9h123.5c12.3 0 21.3-3 27.3-9s9-14.9 9-26.8v-88.3c0-11.9-3-20.8-9-26.8s-15-9-27.3-9z"/></svg>
            </div>`;

        const priceHtml = isOnSale
          ? `<div class="price price--on-sale price--sale-color">
              <div class="price__default">
                <span class="price__from">Desde</span>
                <span class="price__current">${priceFormatted}</span>
                <s class="price__was">${compareAtFormatted}</s>
              </div>
            </div>`
          : `<div class="price price--sale-color">
              <div class="price__default">
                <span class="price__current">${priceFormatted}</span>
              </div>
            </div>`;

        return `
          <li class="slider__item">
            <div class="card card--row card--related relative flex">
              <div class="card__media">
                ${image}
              </div>
              <div class="card__info flex flex-col">
                <p class="h6 regular-text mt-1 mb-1">
                  <a href="${url}" class="card-link text-current">${title}</a>
                </p>
                ${priceHtml}
              </div>
              <div class="card__buttons inline-flex justify-end flex-auto self-start">
                <button type="button"
                  class="btn btn--secondary btn--sm btn--icon tap-target no-js-hidden js-bsc-add-to-cart"
                  data-variant-id="${variantId}"
                  aria-label="Añadir al carrito">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M12.12 20.44H5.6V9.56h12.8v3.73c.06.4.4.69.8.7.44 0 .8-.35.8-.8v-4.5a.792.792 0 0 0-.8-.69H17V6.5C16.9 4 14.7 2 12 2S7 4.09 7 6.67V8H4.71c-.4.04-.71.37-.71.78v12.53a.8.8 0 0 0 .8.69h7.43c.38-.06.67-.39.67-.78 0-.43-.35-.78-.78-.78ZM8.66 6.67c0-1.72 1.49-3.11 3.33-3.11s3.33 1.39 3.33 3.11V8H8.65V6.67Z"></path><path d="M20 17.25h-2.4v-2.5a.817.817 0 0 0-.8-.7c-.44 0-.8.36-.8.8v2.4h-2.5c-.4.06-.7.4-.7.8 0 .44.36.8.8.8H16v2.5c.06.4.4.7.8.7.44 0 .8-.36.8-.8v-2.4h2.5c.4-.06.69-.4.7-.8 0-.44-.35-.8-.8-.8Z"></path></svg>
                </button>
              </div>
            </div>
          </li>
        `;
      }).join('');

      this.querySelectorAll('.js-bsc-add-to-cart').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          if (!variantId) return;
          btn.disabled = true;
          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
            if (res.ok) {
              const cart = await (await fetch('/cart.js')).json();
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
              window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
              window.location.reload();
            }
          } catch (error) {
            console.error('Error añadiendo al carrito:', error);
            btn.disabled = false;
          }
        });
      });
    }
  }

  customElements.define('best-sellers-carousel', BestSellersCarousel);
}